import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Collapse,
  makeStyles,
  Paper,
  Divider,
} from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { Formik } from "formik";

import FormSection from "./FormSection";
import ThankYouMessage from "./ThankYouMessage";
import { submitMediationRequest } from "./mediationApi";
import { buildInitialValues, buildValidationSchema } from "./validationSchema";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
  },
  paper: {
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    textAlign: "left",
  },
  formHeader: {
    padding: theme.spacing(4, 4, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  formLabel: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  formTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
  },
  formSubtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  formBody: {
    padding: theme.spacing(4),
  },
  sectionDivider: {
    margin: theme.spacing(5, 0),
    backgroundColor: theme.palette.divider,
  },
  footer: {
    padding: theme.spacing(3, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  submitButton: {
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: theme.spacing(1.25, 4),
    borderRadius: 0,
    minWidth: 180,
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -10,
    marginLeft: -10,
  },
  requiredNote: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  globalError: {
    display: "flex",
    alignItems: "flex-start",
    gap: '10px',
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${theme.palette.error.light}`,
    backgroundColor: "rgba(211, 47, 47, 0.04)",
    marginBottom: theme.spacing(3),
  },
  globalErrorIcon: {
    color: theme.palette.error.main,
    fontSize: 20,
    marginTop: 1,
    flexShrink: 0,
  },
  globalErrorText: {
    // fontSize: "0.875rem",
    color: theme.palette.error.main,
    // lineHeight: 1.55,
  },
}));

/**
 * MediationRequestForm
 *
 * @param {Array}  schema     - Optional override for the form schema (defaults to mediationFormSchema)
 * @param {string} formTitle  - Optional title override
 * @param {string} formLabel  - Optional label override (appears above title)
 * @param {func}   onSuccess  - Optional callback called with referenceId on success
 */
const MediationRequestForm = ({
  schema,
  formTitle = "Mediation Request",
  formLabel = "Online Dispute Resolution",
  onSuccess,
  FIELD_TYPES,
}) => {
  const classes = useStyles();
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const [globalError, setGlobalError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const validationSchema = buildValidationSchema(schema);
  const initialValues = buildInitialValues(schema);

  const handleSubmit = useCallback(
    async (values, { setSubmitting, setErrors }) => {
      setGlobalError("");

      const result = await submitMediationRequest(values, uploadedFiles);

      if (result.success) {
        setReferenceId(result.referenceId);
        setSubmitted(true);
        if (onSuccess) onSuccess(result.referenceId);
      } else if (result.fieldErrors) {
        // Map server field errors back into Formik
        setErrors(result.fieldErrors);
        // Scroll to first error field
        const firstErrorKey = Object.keys(result.fieldErrors)[0];
        const el = document.getElementById(firstErrorKey);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setGlobalError(result.globalError || "Submission failed. Please try again.");
      }

      setSubmitting(false);
    },
    [uploadedFiles, onSuccess]
  );

  if (submitted) {
    return (
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <ThankYouMessage referenceId={referenceId} contactEmail={"mediation@centre.org"} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        {/* ── Form Header ─────────────────────────────────────────────── */}
        <Box className={classes.formHeader}>
          <Typography className={classes.formLabel}>{formLabel}</Typography>
          <Typography variant="h5" className={classes.formTitle}>
            {formTitle}
          </Typography>
          <Typography className={classes.formSubtitle}>
            Please complete all required fields marked with an asterisk (*). The
            information provided will be used solely for the purpose of facilitating
            the mediation process.
          </Typography>
        </Box>

        {/* ── Form Body ───────────────────────────────────────────────── */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange={false}
        >
          {({ handleSubmit: formikSubmit, isSubmitting }) => (
            <form onSubmit={formikSubmit} noValidate>
              <Box className={classes.formBody}>

                {/* Render sections */}
                {schema.map((section, index) => (
                  <React.Fragment key={section.sectionId}>
                    <FormSection
                      section={section}
                      sectionNumber={index + 1}
                      FIELD_TYPES={FIELD_TYPES}
                      files={
                        section.fields.some((f) => f.type === FIELD_TYPES.FILE_UPLOAD)
                          ? uploadedFiles
                          : undefined
                      }
                      onFilesChange={
                        section.fields.some((f) => f.type === FIELD_TYPES.FILE_UPLOAD)
                          ? setUploadedFiles
                          : undefined
                      }
                    />
                    {index < schema.length - 1 && (
                      <Divider className={classes.sectionDivider} />
                    )}
                  </React.Fragment>
                ))}

                {/* Global Error Banner */}
                <Collapse in={Boolean(globalError)}>
                  {globalError && (
                    <Box className={classes.globalError}>
                      <ErrorOutlineIcon className={classes.globalErrorIcon} />
                      <Typography className={classes.globalErrorText}>
                        {globalError}
                      </Typography>
                    </Box>
                  )}
                </Collapse>
              </Box>

              {/* ── Footer / Submit ───────────────────────────────────── */}
              <Box className={classes.footer}>
                <Typography className={classes.requiredNote}>
                  Fields marked with * are required
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  className={classes.submitButton}
                  disableElevation
                >
                  {isSubmitting ? (
                    <>
                      Submitting…
                      <CircularProgress
                        size={20}
                        className={classes.buttonProgress}
                        color="inherit"
                      />
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default MediationRequestForm;
